"use client";

import { useEffect, useRef, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

export default function CodeBlock({ className = "", children, ...props }) {
    const language =
        (className || "").replace("language-", "").trim() ||
        props["data-language"] ||
        "plaintext";

    const [copied, setCopied] = useState(false);
    const codeRef = useRef(null);

    const text = String(children ?? "").replace(/\n$/, "");

    const handleCopy = async () => {
        try {
            const content = codeRef.current?.textContent ?? text;
            await navigator.clipboard.writeText(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            // Fallback: select + execCommand for very old browsers
            const ta = document.createElement("textarea");
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            ta.remove();
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        }
    };

    // Keep focus visible on keyboard users
    useEffect(() => {
        const btn = document.getElementById("copy-btn-" + hash(text));
        if (!btn) return;
        btn.addEventListener("keyup", (e) => {
            if (e.key === "Enter" || e.key === " ") handleCopy();
        });
        return () => btn.removeEventListener("keyup", handleCopy);
    }, [text]);

    return (
        <div className="relative group my-6">
            <button
                id={"copy-btn-" + hash(text)}
                aria-label="Copy code"
                onClick={handleCopy}
                type="button"
                className="absolute top-2 right-2 z-10 bg-white/90 dark:bg-zinc-800/90 border rounded px-2 py-1 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
                {copied ? (
                    <CheckIcon className="text-green-600 h-4 w-4" />
                ) : (
                    <CopyIcon className="h-4 w-4" />
                )}
                {copied ? "Copied!" : "Copy"}
            </button>

            <SyntaxHighlighter
                language={language}
                style={oneDark}
                customStyle={{
                    borderRadius: 8,
                    fontSize: 15,
                    margin: 0,
                    background: "var(--tw-prose-pre-bg, #282c34)",
                }}
                className="rounded !p-4 overflow-x-auto"
                PreTag="div"
                ref={codeRef}
                {...props}
            >
                {text}
            </SyntaxHighlighter>
        </div>
    );
}

// Simple stable hash to key the copy button
function hash(str) {
    let h = 0,
        i = 0,
        len = str.length;
    while (i < len) {
        h = ((h << 5) - h + str.charCodeAt(i++)) | 0;
    }
    return (h >>> 0).toString(36);
}

function CopyIcon({ className = "" }) {
    return (
        <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className={className}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );
}

function CheckIcon({ className = "" }) {
    return (
        <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className={className}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m5 12 5 5L20 7" />
        </svg>
    );
}
